import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import { useOrgId } from './org';

export interface Account {
  id: string;
  code: string;
  name: string;
  fullName: string;
  class: 'asset' | 'liability' | 'equity' | 'income' | 'expense';
  accountType: string;
  accountTypeName: string;
  detailType: string;
  detailTypeName: string;
  parentId: string | null;
  depth: number;
  description: string | null;
  defaultTaxCodeId: string | null;
  defaultTaxCode: string | null;
  isControl: boolean;
  systemKey: string | null;
  isActive: boolean;
  hasTransactions: boolean;
  balance: string;
  currency: string;
  /** The account's own currency (foreign Bank and Credit card accounts). */
  accountCurrency: string;
  /** Balance in the account's own currency when it is foreign, else null. */
  foreignBalance: string | null;
}

export interface AccountCategory {
  key: string;
  name: string;
  class: string;
  normalBalance: 'debit' | 'credit';
  report: string;
  detailTypes: Array<{ key: string; name: string; description: string | null; isControl: boolean }>;
}

export interface TaxCode {
  id: string;
  code: string;
  name: string;
  kind: 'standard' | 'zero' | 'exempt' | 'out_of_scope' | 'reverse_charge';
  /** Percent as a decimal string ("5.00"). */
  rate: string;
  appliesTo: 'sales' | 'purchases' | 'both';
  returnBox: string | null;
  /** India GST: CGST + SGST or IGST. */
  gstSplit?: 'cgst_sgst' | 'igst' | null;
  effectiveFrom: string;
  effectiveTo: string | null;
  description: string | null;
  isActive: boolean;
  inUse: boolean;
}

export function useAccounts(opts: { includeInactive?: boolean } = {}) {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['accounts', orgId, opts.includeInactive ?? false],
    queryFn: () =>
      api<Account[]>(
        `/orgs/${orgId}/accounts${opts.includeInactive ? '?includeInactive=true' : ''}`,
      ),
    enabled: Boolean(orgId),
  });
}

export function useAccountTypes() {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['account-types', orgId],
    queryFn: () => api<AccountCategory[]>(`/orgs/${orgId}/account-types`),
    enabled: Boolean(orgId),
    staleTime: Infinity,
  });
}

export function useTaxCodes(opts: { includeInactive?: boolean } = {}) {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['tax-codes', orgId, opts.includeInactive ?? false],
    queryFn: () =>
      api<TaxCode[]>(
        `/orgs/${orgId}/tax-codes${opts.includeInactive ? '?includeInactive=true' : ''}`,
      ),
    enabled: Boolean(orgId),
    staleTime: 5 * 60_000,
  });
}

export const TAX_KIND_LABELS: Record<TaxCode['kind'], string> = {
  standard: 'Standard rated',
  zero: 'Zero rated',
  exempt: 'Exempt',
  out_of_scope: 'Out of scope',
  reverse_charge: 'Reverse charge',
};

/** VAT 201 boxes a code reports to (document 07). */
export const RETURN_BOXES: Array<{ value: string; label: string }> = [
  { value: '1', label: '1a–1g · Standard-rated supplies (by emirate)' },
  { value: '2', label: '2 · Tax refunds to tourists' },
  { value: '3', label: '3 · Supplies subject to reverse charge' },
  { value: '4', label: '4 · Zero-rated supplies' },
  { value: '5', label: '5 · Exempt supplies' },
  { value: '6', label: '6 · Goods imported into the UAE' },
  { value: '7', label: '7 · Adjustments to goods imported' },
  { value: '9', label: '9 · Standard-rated expenses' },
  { value: '10', label: '10 · Reverse charge (recoverable)' },
];

/** "5%" or "5.5%" (document 05, Percent). */
export const formatRate = (rate: string): string =>
  `${rate.includes('.') ? rate.replace(/\.?0+$/, '') : rate}%`;

/** Codes for a sales or purchase picker. */
export const taxCodesFor = (codes: TaxCode[] | undefined, side: 'sales' | 'purchases') =>
  (codes ?? []).filter((t) => t.isActive && (t.appliesTo === 'both' || t.appliesTo === side));
